import { SourceTypes } from '../../../../constants';
import {
  getAuthor,
  getDataServiceExcludeContentFilters,
  getDataServicePage,
  getDataServicePostsFetcher,
  getDataServicePostsSelector,
  getPostsURL,
  getSourceStatusURL
} from '../../utils';
import type { ProviderType, UserValue } from '../types';
import type { LinkedinResponseType, LinkedinSourceValue } from './types';
import { linkedinExcludeFiltersKeys } from './utils';

const PLATFORM = SourceTypes.LINKEDIN;

export type LinkedinPreparedSource = LinkedinSourceValue & {
  author?: UserValue;
};

export function createLinkedinSourceProvider(
  sourceValue: LinkedinPreparedSource,
  globalKey: string[],
  filtersKey: string[],
  widgetToken: string,
  updateSourceAuthor?: (sourceId: string, author: UserValue) => void
): ProviderType<LinkedinResponseType> {
  let author: UserValue | undefined;
  const {
    source = '',
    sourcePID = '',
    status,
    _customWidgetDataPostsLimit
  } = sourceValue;
  const excludeFilters = getDataServiceExcludeContentFilters(
    sourceValue,
    linkedinExcludeFiltersKeys
  );

  const excludeFiltersKeys = JSON.stringify(excludeFilters);
  const key = [...globalKey, ...filtersKey, source, excludeFiltersKeys, status];

  const statusURL = getSourceStatusURL(sourcePID);

  const postsURL = getPostsURL({
    sourcePID,
    filters: excludeFilters,
    limit: _customWidgetDataPostsLimit
  });

  const page = getDataServicePage(postsURL);

  const select = getDataServicePostsSelector({ platform: PLATFORM });

  const customSelect = (response: LinkedinResponseType) => {
    const data = select(response);

    return response.payload.map(({ extra }, index) => {
      const currentPost = author
        ? { ...data[index], user: { ...author, username: undefined } }
        : data[index];

      const { repost } = extra;
      if (!repost) {
        return currentPost;
      }

      const [formattedRepost] = select({
        ...response,
        payload: [repost]
      });

      return {
        ...currentPost,
        repost: formattedRepost
      };
    });
  };

  const baseFetch = getDataServicePostsFetcher({
    statusURL,
    status,
    postsURL,
    widgetToken,
    platform: PLATFORM
  });

  const fetch = async () => {
    const response = await baseFetch();

    try {
      author = await getAuthor(sourceValue);

      if (author && updateSourceAuthor) {
        updateSourceAuthor(sourceValue.id, author);
      }
    } catch (error) {
      console.error('Failed to load LinkedIn author:', error);
    }

    return response;
  };

  return { key, page, select: customSelect, fetch };
}
